import type { ReactNode } from 'react';
import { formatCount, formatSignedCount, formatSignedPercent } from '@/lib/format';
import type { Baseline } from '@/store/useFunnelStore';

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="drawer-section">
      <h3>{title}</h3>
      {children}
    </section>
  );
}

export function Readout({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="readout">
      <span>{label}</span>
      <strong>{value}</strong>
      {note && <em>{note}</em>}
    </div>
  );
}

export function DerivedNote({ rows }: { rows: Array<[string, string]> }) {
  return (
    <div className="derived-note">
      {rows.map(([label, value]) => (
        <div key={label} className="derived-row">
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  );
}

export function Note({ children, tone = 'info' }: { children: ReactNode; tone?: 'info' | 'warn' }) {
  return <div className={`drawer-note tone-${tone}`}>{children}</div>;
}

export type Provenance = 'entered' | 'sourced' | 'assumption' | 'derived';

const PROVENANCE_LABEL: Record<Provenance, string> = {
  entered: 'Entered',
  sourced: 'Sourced',
  assumption: 'Assumption',
  derived: 'Derived',
};

export function ProvenanceTag({ kind }: { kind: Provenance }) {
  return <span className={`provenance is-${kind}`}>{PROVENANCE_LABEL[kind]}</span>;
}

/** Change since the drawer opened, so an edit shows what it bought. */
export function ImpactBoxes({
  baseline,
  completed,
  screeningRate,
}: {
  baseline: Baseline | null;
  completed: number;
  screeningRate: number;
}) {
  const completedDelta = baseline ? completed - baseline.completed : 0;
  const rateDelta = baseline ? screeningRate - baseline.screeningRate : 0;
  const tone = (delta: number) => (delta > 0.5 ? 'is-up' : delta < -0.5 ? 'is-down' : '');

  return (
    <>
      <div className="drawer-impact">
        <div className="impact-box">
          <span>Completed follow-ups</span>
          <strong className={tone(completedDelta)}>{formatSignedCount(completedDelta)}</strong>
        </div>
        <div className="impact-box">
          <span>Screening rate</span>
          <strong className={tone(rateDelta * 1000)}>{formatSignedPercent(rateDelta)}</strong>
        </div>
      </div>
      <p className="impact-caption">
        Change since this drawer opened. Now at {formatCount(completed)} completed.
      </p>
    </>
  );
}
